import type { AppIconName } from './appIcons'

/**
 * Module Registry - Single Source of Truth for all modules/features
 * 
 * SOLID Principles:
 * - Single Responsibility: Each module config defines one feature
 * - Open/Closed: Easy to add new modules without changing existing code
 * - DRY: No duplication of module metadata across the app
 * 
 * This registry is used by:
 * - App.tsx (dynamic routing)
 * - Sidebar.tsx (dynamic navigation)
 * - Dashboard (module display)
 * - usePermissions (permission constants)
 */

export type MarketplaceType = 'allegro' | 'decathlon' | 'castorama' | 'leroymerlin' | 'emag' | 'general'

/** Sidebar subsections within a marketplace (e.g. Allegro). */
export type NavGroupId =
  | 'offer_improvement'
  | 'offer_listing'
  | 'additional_modules'
  | 'alerts'

export interface NavGroupConfig {
  id: NavGroupId
  /** i18n key under dashboard ns */
  labelKey: string
  order: number
}

export const NAV_GROUPS: Record<NavGroupId, NavGroupConfig> = {
  offer_improvement: {
    id: 'offer_improvement',
    labelKey: 'sidebar.navGroups.offerImprovement',
    order: 1,
  },
  offer_listing: {
    id: 'offer_listing',
    labelKey: 'sidebar.navGroups.offerListing',
    order: 2,
  },
  additional_modules: {
    id: 'additional_modules',
    labelKey: 'sidebar.navGroups.additionalModules',
    order: 3,
  },
  alerts: {
    id: 'alerts',
    labelKey: 'sidebar.navGroups.alerts',
    order: 4,
  },
}

export interface ModuleConfig {
  // Core identification
  id: string // matches MODULES constant
  /** i18n key under dashboard ns, e.g. modules.dashboard.name */
  name: string
  /** i18n key under dashboard ns, e.g. modules.dashboard.description */
  description: string
  
  // Visual — icon keys from config/appIcons.tsx
  icon: AppIconName
  marketplace: MarketplaceType
  marketplaceIcon: AppIconName
  
  // Routing
  route: string
  legacyRoutes?: string[] // Old routes for backward compatibility
  
  // Display settings
  showInDashboard: boolean
  showInSidebar: boolean
  order: number // Display order within marketplace group / navGroup
  isCore: boolean // Core modules (dashboard, konta_marketplace, profil) always accessible
  
  // Status
  status: 'ready' | 'beta' | 'new' | 'mvp' | 'refreshed' | 'coming_soon'

  /** Optional sidebar subsection within the marketplace group. */
  navGroup?: NavGroupId

  /** Standalone primary module in marketplace nav (e.g. Zarządzanie ofertami). */
  primaryInNav?: boolean
  
  // Module dependencies (auto-granted)
  dependencies?: string[]
  
  // Permission requirement (for routes)
  requirePermission?: boolean // If false, route is public (default: true)

  // Navigation visibility without changing permission grants (frontend-only)
  alwaysVisibleInNav?: boolean

  /** Tester rollout: /auth/me/permissions exposes manual admin grant only (see backend MANUAL_GRANT_PERMISSION_MODULES). */
  manualGrantOnly?: boolean

  /** Key under /config/feature-flags `modules` that must be on (admins bypass). See isModuleFlagOpen. */
  featureFlag?: string
}

/**
 * Marketplace configurations
 */
export const MARKETPLACES = {
  allegro: {
    name: 'Allegro',
    icon: 'marketplace-allegro' as AppIconName,
    color: '#FF5A00',
    letter: 'A',
    order: 1,
  },
  decathlon: {
    name: 'Decathlon',
    icon: 'marketplace-decathlon' as AppIconName,
    color: '#0082C3',
    letter: 'D',
    order: 2,
  },
  castorama: {
    name: 'Castorama',
    icon: 'marketplace-castorama' as AppIconName,
    color: '#F5A800',
    letter: 'C',
    order: 3,
  },
  leroymerlin: {
    name: 'Leroy Merlin',
    icon: 'marketplace-leroymerlin' as AppIconName,
    color: '#67AE3E',
    letter: 'L',
    order: 4,
  },
  emag: {
    name: 'eMAG',
    icon: 'marketplace-emag' as AppIconName,
    color: '#8B3FC8',
    letter: 'E',
    order: 5,
  },
  general: {
    name: 'General',  // i18n: dashboard:marketplaces.general
    icon: 'marketplace-general' as AppIconName,
    color: '#3a3a3a',
    letter: 'O',
    order: 6,
  },
} as const

/**
 * Module Registry - All modules defined here
 */
export const MODULE_REGISTRY: ModuleConfig[] = [
  // === CORE MODULES ===
  {
    id: 'dashboard',
    name: 'modules.dashboard.name',
    description: 'modules.dashboard.description',
    icon: 'house',
    marketplace: 'general',
    marketplaceIcon: MARKETPLACES.general.icon,
    route: '/dashboard',
    showInDashboard: false, // Dashboard itself is not shown in dashboard
    showInSidebar: false, // Shown separately in sidebar
    order: -1,
    isCore: true,
    status: 'ready',
    requirePermission: false // Core module, always accessible
  },
  {
    id: 'konta_marketplace',
    name: 'modules.konta_marketplace.name',
    description: 'modules.konta_marketplace.description',
    icon: 'intersect',
    marketplace: 'general',
    marketplaceIcon: MARKETPLACES.general.icon,
    route: '/accounts',
    showInDashboard: false, // Utility feature, not a primary workflow
    showInSidebar: true,
    order: 0,
    isCore: true,
    status: 'ready',
    requirePermission: false
  },
  {
    id: 'credits',
    name: 'modules.credits.name',
    description: 'modules.credits.description',
    icon: 'coins',
    marketplace: 'general',
    marketplaceIcon: MARKETPLACES.general.icon,
    route: '/credits',
    showInDashboard: false,
    showInSidebar: true,
    order: 1,
    isCore: true,
    status: 'ready',
    requirePermission: false
  },
  {
    id: 'subscriptions',
    name: 'modules.subscriptions.name',
    description: 'modules.subscriptions.description',
    icon: 'credit-card',
    marketplace: 'general',
    marketplaceIcon: MARKETPLACES.general.icon,
    route: '/subscriptions',
    showInDashboard: false,
    showInSidebar: true,
    order: 2,
    isCore: true,
    status: 'ready',
    requirePermission: false
  },
  {
    id: 'referrals',
    name: 'modules.referrals.name',
    description: 'modules.referrals.description',
    icon: 'gift',
    marketplace: 'general',
    marketplaceIcon: MARKETPLACES.general.icon,
    route: '/referrals',
    showInDashboard: false,
    showInSidebar: true,
    order: 3,
    isCore: true,
    status: 'ready',
    requirePermission: false
  },
  {
    id: 'szkola_ofertatora',
    name: 'modules.szkola_ofertatora.name',
    description: 'modules.szkola_ofertatora.description',
    icon: 'graduation-cap',
    marketplace: 'general',
    marketplaceIcon: MARKETPLACES.general.icon,
    route: '/school',
    showInDashboard: false,
    showInSidebar: true,
    order: 4,
    isCore: true,
    status: 'ready',
    requirePermission: false
  },
  
  // === ALLEGRO MODULES ===
  // order: primary standalone → improvement 20–29 → listing 30 → additional 35–39 → alerts 40–49
  // --- Primary (standalone) ---
  {
    id: 'allegro_zarzadzanie_ofertami',
    name: 'modules.allegro_zarzadzanie_ofertami.name',
    description: 'modules.allegro_zarzadzanie_ofertami.description',
    icon: 'gear',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/offer-management',
    showInDashboard: true,
    showInSidebar: true,
    order: 1,
    isCore: false,
    status: 'refreshed',
    primaryInNav: true,
    alwaysVisibleInNav: true,
    manualGrantOnly: true,
  },
  // --- Dodatkowe moduły (before Alerty) ---
  {
    id: 'allegro_kopiowanie_ofert',
    name: 'modules.allegro_kopiowanie_ofert.name',
    description: 'modules.allegro_kopiowanie_ofert.description',
    icon: 'clipboard',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/copy-offers',
    legacyRoutes: ['/copy-offers'],
    showInDashboard: true,
    showInSidebar: true,
    order: 35,
    isCore: true,
    status: 'ready',
    navGroup: 'additional_modules',
  },
  {
    id: 'allegro_wielopaki',
    name: 'modules.allegro_wielopaki.name',
    description: 'modules.allegro_wielopaki.description',
    icon: 'git-fork',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/multipacks',
    showInDashboard: true,
    showInSidebar: true,
    order: 36,
    isCore: true,
    status: 'ready',
    requirePermission: false,
    navGroup: 'additional_modules',
  },
  {
    id: 'allegro_zestawy_elastyczne',
    name: 'modules.allegro_zestawy_elastyczne.name',
    description: 'modules.allegro_zestawy_elastyczne.description',
    icon: 'puzzle-piece',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/flexible-bundles',
    showInDashboard: true,
    showInSidebar: true,
    order: 37,
    isCore: true,
    status: 'ready',
    requirePermission: false,
    navGroup: 'additional_modules',
  },
  // --- Poprawianie ofert ---
  {
    id: 'allegro_opisomat',
    name: 'modules.allegro_opisomat.name',
    description: 'modules.allegro_opisomat.description',
    icon: 'file-text',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/opisomat',
    showInDashboard: true,
    showInSidebar: true,
    order: 20,
    isCore: true,
    status: 'ready',
    navGroup: 'offer_improvement',
  },
  {
    id: 'allegro_tytuly_v2',
    name: 'modules.allegro_tytuly_v2.name',
    description: 'modules.allegro_tytuly_v2.description',
    icon: 'text-t',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/titles-v2',
    showInDashboard: true,
    showInSidebar: true,
    order: 21,
    isCore: false,
    status: 'ready',
    requirePermission: false,
    navGroup: 'offer_improvement',
  },
  {
    id: 'allegro_parametry',
    name: 'modules.allegro_parametry.name',
    description: 'modules.allegro_parametry.description',
    icon: 'package',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/parameters',
    showInDashboard: true,
    showInSidebar: true,
    order: 22,
    isCore: false,
    status: 'ready',
    requirePermission: false,
    navGroup: 'offer_improvement',
  },
  {
    id: 'allegro_grafikomat',
    name: 'modules.allegro_grafikomat.name',
    description: 'modules.allegro_grafikomat.description',
    icon: 'image',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/grafikomat',
    showInDashboard: true,
    showInSidebar: true,
    order: 23,
    isCore: false,
    status: 'ready',
    requirePermission: false,
    navGroup: 'offer_improvement',
  },
  {
    id: 'allegro_kompleksowe',
    name: 'modules.allegro_kompleksowe.name',
    description: 'modules.allegro_kompleksowe.description',
    icon: 'lightbulb',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/comprehensive-fix',
    showInDashboard: true,
    showInSidebar: true,
    order: 24,
    isCore: false,
    status: 'ready',
    requirePermission: false,
    navGroup: 'offer_improvement',
  },
  // --- Wystawianie ofert ---
  {
    id: 'allegro_wystawianie_od_zera',
    name: 'modules.allegro_wystawianie_od_zera.name',
    description: 'modules.allegro_wystawianie_od_zera.description',
    icon: 'plus-circle',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/listing-from-scratch',
    showInDashboard: true,
    showInSidebar: true,
    order: 30,
    isCore: false,
    status: 'beta',
    requirePermission: false,
    navGroup: 'offer_listing',
  },
  // --- Alerty (public) ---
  {
    id: 'allegro_alerty_opinie',
    name: 'modules.allegro_alerty_opinie.name',
    description: 'modules.allegro_alerty_opinie.description',
    icon: 'list-star',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/alerty-oceny-produktow',
    showInDashboard: true,
    showInSidebar: true,
    order: 40,
    isCore: false,
    status: 'beta',
    requirePermission: false,
    navGroup: 'alerts',
  },
  {
    id: 'allegro_alerty_jakosc',
    name: 'modules.allegro_alerty_jakosc.name',
    description: 'modules.allegro_alerty_jakosc.description',
    icon: 'chart-line',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/alerty-jakosc',
    showInDashboard: true,
    showInSidebar: true,
    order: 41,
    isCore: false,
    status: 'beta',
    requirePermission: false,
    navGroup: 'alerts',
  },
  {
    id: 'allegro_alerty_oceny_konta',
    name: 'modules.allegro_alerty_oceny_konta.name',
    description: 'modules.allegro_alerty_oceny_konta.description',
    icon: 'star',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/alerty-oceny-konta',
    showInDashboard: true,
    showInSidebar: true,
    order: 42,
    isCore: false,
    status: 'beta',
    requirePermission: false,
    navGroup: 'alerts',
  },
  {
    id: 'allegro_ads',
    name: 'modules.allegro_ads.name',
    description: 'modules.allegro_ads.description',
    icon: 'megaphone',
    marketplace: 'allegro',
    marketplaceIcon: MARKETPLACES.allegro.icon,
    route: '/allegro/ads',
    showInDashboard: true,
    showInSidebar: true,
    order: 6.7,
    isCore: false,
    status: 'beta',
    manualGrantOnly: true,
    featureFlag: 'allegro_ads',
  },

  // === eMAG MODULES ===
  {
    id: 'emag_opisomat',
    name: 'modules.emag_opisomat.name',
    description: 'modules.emag_opisomat.description',
    icon: 'file-text',
    marketplace: 'emag',
    marketplaceIcon: MARKETPLACES.emag.icon,
    route: '/emag/opisomat',
    showInDashboard: true,
    showInSidebar: true,
    order: 1,
    isCore: false,
    status: 'mvp',
    // Permission-gated (no requirePermission: false) — same as Grafikomat.
  },
  {
    id: 'emag_tytulomat',
    name: 'modules.emag_tytulomat.name',
    description: 'modules.emag_tytulomat.description',
    icon: 'text-t',
    marketplace: 'emag',
    marketplaceIcon: MARKETPLACES.emag.icon,
    route: '/emag/tytulomat',
    showInDashboard: true,
    showInSidebar: true,
    order: 2,
    isCore: false,
    status: 'mvp',
  },
  {
    id: 'emag_wystawianie_ofert',
    name: 'modules.emag_wystawianie_ofert.name',
    description: 'modules.emag_wystawianie_ofert.description',
    icon: 'package',
    marketplace: 'emag',
    marketplaceIcon: MARKETPLACES.emag.icon,
    route: '/emag/listing-from-allegro',
    showInDashboard: true,
    showInSidebar: true,
    order: 0,
    isCore: false,
    status: 'mvp',
  },
]

/**
 * Utility functions for working with modules
 */

export const getModuleById = (id: string): ModuleConfig | undefined => {
  return MODULE_REGISTRY.find(m => m.id === id)
}

/**
 * True when a module is visible and accessible to ALL authenticated users
 * without an explicit permission grant.
 *
 * Single source of truth used by authStore.hasPermission, usePermissions, and
 * PermissionGuard so that adding requirePermission:false here is the only
 * change required to make a module public (DRY / SOLID DIP).
 */
export const isModulePublic = (m: ModuleConfig | undefined): boolean => {
  if (!m) return false
  return m.isCore === true || m.requirePermission === false
}

export type ModulePermissionChecker = (moduleId: string) => boolean

/**
 * Whether a module should appear in nav/dashboard chrome for this user.
 * Public + alwaysVisibleInNav (locked teaser) + explicit grant.
 * Single policy used by Sidebar, Home, AddAccountModal (DIP / DRY).
 */
export const isModuleVisibleToUser = (
  module: ModuleConfig,
  hasPermission: ModulePermissionChecker,
): boolean => {
  if (isModulePublic(module)) return true
  if (module.alwaysVisibleInNav) return true
  return hasPermission(module.id)
}

export const filterModulesByUserAccess = (
  modules: ModuleConfig[],
  hasPermission: ModulePermissionChecker,
): ModuleConfig[] => modules.filter((m) => isModuleVisibleToUser(m, hasPermission))

export type ModuleFlagChecker = (flag: string) => boolean

/**
 * Feature-flag gate for modules with `featureFlag`. Admins bypass so beta modules
 * can be QA'd before the global flag flips. Orthogonal to permission grants.
 */
export const isModuleFlagOpen = (
  module: ModuleConfig,
  opts: { isModuleFlagEnabled: ModuleFlagChecker; isAdmin: boolean },
): boolean => {
  if (!module.featureFlag) return true
  return opts.isAdmin || opts.isModuleFlagEnabled(module.featureFlag)
}

/**
 * Marketplace chrome (sidebar group, connect-account card) is shown when the
 * marketplace flag is on AND the user can see at least one module for it.
 * Marketplaces with no registered modules fall back to flag-only.
 */
export const isMarketplaceAccessibleToUser = (
  marketplace: string,
  opts: {
    isMarketplaceEnabled: (m: string) => boolean
    hasPermission: ModulePermissionChecker
    modules?: ModuleConfig[]
  },
): boolean => {
  if (marketplace === 'general') return true
  if (!opts.isMarketplaceEnabled(marketplace)) return false

  const pool = (opts.modules ?? MODULE_REGISTRY).filter(
    (m) => m.marketplace === marketplace,
  )
  if (pool.length === 0) return true
  return pool.some((m) => isModuleVisibleToUser(m, opts.hasPermission))
}

export const getModulesByMarketplace = (marketplace: MarketplaceType): ModuleConfig[] => {
  return MODULE_REGISTRY
    .filter(m => m.marketplace === marketplace)
    .sort((a, b) => a.order - b.order)
}

/**
 * Filter modules by marketplace enabled status
 * This function should be called with feature flag context
 */
export const filterModulesByMarketplace = (
  modules: ModuleConfig[],
  isMarketplaceEnabled: (marketplace: string) => boolean
): ModuleConfig[] => {
  return modules.filter(module => {
    // Always show core modules
    if (module.isCore) return true
    
    // For general modules, always show
    if (module.marketplace === 'general') return true
    
    // Check if marketplace is enabled
    return isMarketplaceEnabled(module.marketplace)
  })
}

export const getDashboardModules = (): ModuleConfig[] => {
  return MODULE_REGISTRY
    .filter(m => m.showInDashboard)
    .sort((a, b) => {
      // Sort by marketplace first, then by order
      const marketplaceOrderA = MARKETPLACES[a.marketplace].order
      const marketplaceOrderB = MARKETPLACES[b.marketplace].order
      
      if (marketplaceOrderA !== marketplaceOrderB) {
        return marketplaceOrderA - marketplaceOrderB
      }
      
      return a.order - b.order
    })
}

export const getSidebarModules = (): ModuleConfig[] => {
  return MODULE_REGISTRY
    .filter(m => m.showInSidebar)
    .sort((a, b) => a.order - b.order)
}

export const getModulesByIds = (ids: string[]): ModuleConfig[] => {
  return MODULE_REGISTRY.filter(m => ids.includes(m.id))
}

/**
 * Group modules by marketplace for display
 */
export const getGroupedModules = (modules: ModuleConfig[]) => {
  const grouped = new Map<MarketplaceType, ModuleConfig[]>()
  
  modules.forEach(module => {
    if (!grouped.has(module.marketplace)) {
      grouped.set(module.marketplace, [])
    }
    grouped.get(module.marketplace)!.push(module)
  })
  
  // Sort each group by order
  grouped.forEach((moduleList) => {
    moduleList.sort((a, b) => a.order - b.order)
  })
  
  return grouped
}

export type NavGroupBucket = {
  group: NavGroupConfig | null
  modules: ModuleConfig[]
}

/**
 * Split marketplace modules into sidebar subsections (navGroup).
 * Modules without navGroup (e.g. primary standalone) come first.
 */
export const groupModulesByNavGroup = (modules: ModuleConfig[]): NavGroupBucket[] => {
  const byGroup = new Map<NavGroupId, ModuleConfig[]>()
  const ungrouped: ModuleConfig[] = []

  for (const module of modules) {
    if (!module.navGroup) {
      ungrouped.push(module)
      continue
    }
    const list = byGroup.get(module.navGroup) ?? []
    list.push(module)
    byGroup.set(module.navGroup, list)
  }

  const buckets: NavGroupBucket[] = []

  if (ungrouped.length > 0) {
    buckets.push({
      group: null,
      modules: [...ungrouped].sort((a, b) => a.order - b.order),
    })
  }

  buckets.push(
    ...Array.from(byGroup.entries())
      .sort(([a], [b]) => NAV_GROUPS[a].order - NAV_GROUPS[b].order)
      .map(([id, list]) => ({
        group: NAV_GROUPS[id],
        modules: [...list].sort((a, b) => a.order - b.order),
      })),
  )

  return buckets
}

/**
 * Get all routes (including legacy) for a module
 */
export const getAllRoutesForModule = (module: ModuleConfig): string[] => {
  return [module.route, ...(module.legacyRoutes || [])]
}

/**
 * Generate type-safe module constants from registry
 * Used by usePermissions hook
 */
export const generateModuleConstants = () => {
  const constants: Record<string, string> = {}
  
  MODULE_REGISTRY.forEach(module => {
    // Convert id to CONSTANT_CASE (e.g., allegro_edytor_ofert -> ALLEGRO_EDYTOR_OFERT)
    const constantName = module.id.toUpperCase()
    constants[constantName] = module.id
  })
  
  return constants
}
